import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Typography } from 'Components/Typography';

import useResponsiveVariant from 'utils/useResponsiveVariant';

import { DesktopInfoTooltip, MobileInfoIconTooltip } from '../InfoIconTooltip';
import type { infoBlockContentType } from './type';

const InfoWrapper = styled.div<{ color?: string }>`
  color: ${({ color }) => color || 'inherit'};
  font-family: ${theme.typography.fontFamily.body};
  line-height: ${theme.typography.lineHeight['4xs']};
  padding: 0 ${theme.spacing['4x']};
  padding-top: ${theme.spacing['8x']};
  ${legacyTheme.breakpoints.up('sm')} {
    padding: 0;
  }
  ${legacyTheme.breakpoints.up('md')} {
    width: 100%;
    padding: 0;
  }
`;

const EyeBrowText = styled(Typography)`
  /* TODO: @nelly: font-size not in theme */
  font-size: 12px;
  color: inherit;
  font-family: ${theme.typography.fontFamily.label};
  font-weight: ${theme.typography.fontWeight.normal};
  margin-bottom: ${theme.spacing['6x']};
  text-transform: uppercase;
  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: ${theme.spacing['1x']};
  }
  ${legacyTheme.breakpoints.up('md')} {
    margin-bottom: 0;
  }
`;

const InfoTitle = styled(Typography)`
  color: inherit;
  font-family: inherit;
  font-size: ${theme.typography.fontSize.xl};
  margin-bottom: ${theme.spacing['1x']};
  font-weight: ${theme.typography.fontWeight.light};
  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: ${theme.spacing['1x']};
    font-size: ${theme.typography.fontSize.xl};
  }
  ${legacyTheme.breakpoints.up('md')} {
    margin-bottom: 0;
    font-size: ${theme.typography.fontSize.xl};
  }
`;

const InfoSubtitle = styled(Typography)`
  color: inherit;
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.light};
  line-height: ${theme.typography.lineHeight['4xs']};
  ${legacyTheme.breakpoints.up('sm')} {
    width: 85%;
    font-size: ${theme.typography.fontSize.md};
  }
  ${legacyTheme.breakpoints.up('md')} {
    width: 45%;
  }
`;

const TitleAndSubtitleWrapper = styled.div`
  ${legacyTheme.breakpoints.up('md')} {
    display: flex;
    gap: ${theme.spacing['4x']};
    align-items: center;
    justify-content: space-between;
  }
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${theme.colors.primary[400]};
  margin: ${theme.spacing['6x']} 0;
`;

const InfoContainer = styled.span`
  position: relative;
  > button {
    padding-left: ${theme.spacing['1x']};
    vertical-align: bottom;
  }
`;

const DesktopTooltipContainer = styled.span`
  position: absolute;
  top: 1px;
`;

type TextInfoBlockProps = {
  textData: infoBlockContentType;
  color?: string;
  setIsOpen?: () => void;
};

export const TextInfoBlock = ({ textData, color = 'inherit', setIsOpen }: TextInfoBlockProps) => {
  const { isDesktop, isMobile, isTablet } = useResponsiveVariant();
  return (
    <InfoWrapper color={color}>
      <EyeBrowText>{textData.eyeBrow}</EyeBrowText>
      {isDesktop && <Separator />}
      <TitleAndSubtitleWrapper>
        <InfoTitle>{textData.title}</InfoTitle>
        <InfoSubtitle>
          {textData.subtitle}
          <InfoContainer>
            {isMobile && <MobileInfoIconTooltip iconColor="dark" setIsOpen={setIsOpen} size={18} />}
            {isTablet && <MobileInfoIconTooltip setIsOpen={setIsOpen} />}
            {isDesktop && (
              <DesktopTooltipContainer>
                <DesktopInfoTooltip
                  arrowShift={0}
                  iconColor="black"
                  size={16}
                  tooltipContent={textData.tooltip}
                  topPlus={-15}
                />
              </DesktopTooltipContainer>
            )}
          </InfoContainer>
        </InfoSubtitle>
      </TitleAndSubtitleWrapper>
    </InfoWrapper>
  );
};
